import { create } from "zustand";

export type ThemePreference = "light" | "dark" | "system";
type ResolvedTheme = "light" | "dark";

function getSystemTheme(): ResolvedTheme {
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === "system" ? getSystemTheme() : preference;
}

function getInitialPreference(): ThemePreference {
  const saved = localStorage.getItem("theme");
  if (saved === "light" || saved === "dark" || saved === "system") return saved;
  return "system";
}

function applyTheme(resolved: ResolvedTheme) {
  document.documentElement.classList.add("theme-switching");
  document.documentElement.classList.toggle("dark", resolved === "dark");
  // Forced reflow: flushes the class change so the transition actually runs.
  void document.documentElement.offsetHeight;
  requestAnimationFrame(() => {
    document.documentElement.classList.remove("theme-switching");
  });
}

interface ThemeState {
  preference: ThemePreference;
  theme: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

export const useThemeStore = create<ThemeState>((set) => ({
  preference: getInitialPreference(),
  theme: resolveTheme(getInitialPreference()),
  setPreference: (preference: ThemePreference) => {
    const resolved = resolveTheme(preference);
    localStorage.setItem("theme", preference);
    applyTheme(resolved);
    set({ preference, theme: resolved });
  },
}));

// Apply initial theme on module load
applyTheme(resolveTheme(getInitialPreference()));

// Listen for OS theme changes so "system" preference stays in sync
window
  .matchMedia("(prefers-color-scheme: dark)")
  .addEventListener("change", () => {
    const { preference } = useThemeStore.getState();
    if (preference === "system") {
      const resolved = getSystemTheme();
      applyTheme(resolved);
      useThemeStore.setState({ theme: resolved });
    }
  });
